import { useCallback } from 'react';
import { useCanvasImageUploadHandler } from 'components/editor/hooks/useCanvasImageUploadHandler';
import { useActiveEditor } from '../ActiveEditorContext';
import type { InlineImageStorage } from '../extensions/inline-image';
import { INLINE_IMAGE_NODE_NAME } from '../extensions/inline-image';
import { insertInlineImageFromSrc } from '../extensions/inline-image/insertInlineImage';

// Inserts an image picked from the image library at the cursor of the text editor being edited.
// Resolves to false when no editor can take an inline image, so the caller can fall back to adding an image block.
export const useInsertInlineImageIntoActiveEditor = () => {
  const { activeEditor: editor, isEditMode } = useActiveEditor();
  const { handleImageUrlUpload } = useCanvasImageUploadHandler();

  return useCallback(
    async (rawSrc: string): Promise<boolean> => {
      const storage = editor?.storage[INLINE_IMAGE_NODE_NAME] as InlineImageStorage | undefined;
      if (!editor || editor.isDestroyed || !editor.isEditable || !isEditMode || !storage?.isInsertEnabled) {
        return false;
      }

      const src = await handleImageUrlUpload(rawSrc);
      if (!src || editor.isDestroyed) return true;

      await insertInlineImageFromSrc(editor, src, editor.state.selection.to);
      return true;
    },
    [editor, isEditMode, handleImageUrlUpload]
  );
};
